import { useQuery } from "@tanstack/react-query";
import useFetch from "../lib/useFetch";
import { IDeliveryRegionsGeoJSONCollection } from "../types/deliveryRegion";

const API = process.env.REACT_APP_API;

/**
 * Interface para bounds do mapa (bounding box)
 */
export interface IMapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Hook para buscar regiões de entrega dentro de um bounding box (viewport)
 * Usa carregamento incremental baseado na área visível do mapa
 * Os polígonos são simplificados baseados no nível de zoom para melhor performance
 */
export const useDeliveryRegionsInBounds = (
  bounds: IMapBounds | null,
  zoom: number | null,
  estado?: string,
) => {
  const fetch = useFetch();

  return useQuery({
    queryKey: ["delivery-regions-in-bounds", bounds, zoom, estado],
    queryFn: async () => {
      if (!bounds) {
        throw new Error("Bounds não fornecidos");
      }

      if (zoom === null || zoom === undefined) {
        throw new Error("Zoom não fornecido");
      }

      const searchParams = new URLSearchParams({
        north: bounds.north.toString(),
        south: bounds.south.toString(),
        east: bounds.east.toString(),
        west: bounds.west.toString(),
        zoom: zoom.toString(),
      });

      if (estado) {
        searchParams.append("estado", estado);
      }

      try {
        const response = await fetch(
          `${API}/api/semeadora/delivery-regions/in-bounds/?${searchParams.toString()}`,
          {
            method: "GET",
          },
        );
        return response as IDeliveryRegionsGeoJSONCollection;
      } catch (error) {
        console.error("Erro ao buscar regiões no viewport:", error);
        throw new Error(
          "Não foi possível carregar as regiões de entrega. Por favor, tente novamente.",
        );
      }
    },
    enabled: !!bounds && zoom !== null && zoom !== undefined, // Só executa se bounds e zoom estiverem definidos
    keepPreviousData: true, // Evita flicker durante transições
    staleTime: 30000, // Cache de 30 segundos
    refetchOnWindowFocus: false, // Não refaz fetch ao focar na janela
    retry: 2, // Tenta novamente até 2 vezes em caso de erro
    retryDelay: 1000, // Espera 1 segundo entre tentativas
  });
};
